import { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
	Alert,
	Button,
	Divider,
	Drawer,
	Flex,
	Input,
	Modal,
	Segmented,
	Select,
	Table,
	Typography,
} from 'antd';
import { LuFileDown, LuSave, LuSettings, LuTrash2 } from 'react-icons/lu';

import { useProject } from '@contexts/Project.context';
import { useCollectionStore } from '@stores/useCollectionStore';
import { formatArea } from '@utils/measurementSystem';
import { formatNumber } from '@utils/helpers';
import {
	AVAILABLE_PROPERTIES,
	describeCoverageGaps,
	getSimpleFilterCoverageGaps,
	getMatrixFilterCoverageGaps,
} from '@routes/userRoutes/projects/singleProject/components/modifyLayers/hexagonColorFilter/matrixFilter/matrixClassification';
import useCurrencies from '@routes/userRoutes/projects/singleProject/components/modifyLayers/hexagonColorFilter/matrixFilter/useCurrencies';

import SidebarTemplate from '../../../../sidebars/sidebarElements/SidebarTemplate';
import { COLLECTION_SIDEBAR_IDS, DEFAULT_CURRENCY } from '../../constants';
import {
	getAvailableProperties,
	getPropertyRange,
	getPerBandThresholdRanges,
} from '../../utils/hexagonClassificationProperties';
import { collectionDspSummary } from '../../utils/collectionDspSummary';
import useHexagonFilterTemplates from '../../hooks/useHexagonFilterTemplates';
import useDspExport from '../../hooks/useDspExport';
import SimpleEditor from './SimpleEditor';
import MatrixEditor from './MatrixEditor';
import CostSummary from './CostSummary';
import FilterEditorHeader from './FilterEditorHeader';
import CollectionExportReportModal from './CollectionExportReportModal';
import ClassLabelCell from './ClassLabelCell';

const { Text } = Typography;

const MODE_OPTIONS = [
	{ value: 'matrix', label: 'Matrix (2D)' },
	{ value: 'simple', label: 'Simple (1D)' },
];

// DSP templates carry no cell size — a plot's size is its image footprint, not a
// caller choice — so 0 is saved in the template's hexagonSize field.
const NO_CELL_SIZE = 0;

// Classification Results table columns. "Plots" rather than "Count" because a
// row counts sample plots, not detections, and "Footprint" rather than "Area"
// because summed image footprints overlap and are not surveyed ground.
//
// Two area columns, because a plot has two areas and only one of them is priced:
// Footprint is the surveyed image area the plot covers (the container), while
// Object area is the detected object area within it (summed sum_object_area) —
// the quantity Cost Summary multiplies by the price per m². They are shown side
// by side so the cost total has a visible on-screen basis; reading the cost off
// Footprint would overstate it several-fold.
//
// The share column is labelled "% of plots" rather than a bare "%" because it
// sits beside those area columns but is a share of Plots (see
// useHexagonClassification's percentageBasis: 'count'). Plot footprints vary in
// size, so unlike the hexagon table — where uniform cells make a count share and
// an area share the same number — the two genuinely differ here and the basis has
// to be stated. It remains a share of plot count, not of either area.
//
// No dependencies, so a plain module-level constant.
const RESULTS_COLUMNS = [
	{
		title: 'Class',
		dataIndex: 'label',
		key: 'label',
		render: (label, row) => <ClassLabelCell label={label} color={row.color} />,
	},
	{
		title: 'Plots',
		dataIndex: 'count',
		key: 'count',
		align: 'left',
		render: count => formatNumber(count),
	},
	{
		title: 'Footprint',
		dataIndex: 'area',
		key: 'area',
		align: 'left',
		render: area => {
			const [areaNum, areaUnit] = formatArea(area, 1, false);
			return `${areaNum} ${areaUnit}`;
		},
	},
	{
		title: 'Object area',
		dataIndex: 'objectArea',
		key: 'objectArea',
		align: 'left',
		render: objectArea => {
			const [areaNum, areaUnit] = formatArea(objectArea, 1, false);
			return `${areaNum} ${areaUnit}`;
		},
	},
	{
		title: '% of plots',
		dataIndex: 'sharePercentage',
		key: 'sharePercentage',
		align: 'left',
		render: pct => `${pct}%`,
	},
];

/**
 * Color-filter sidebar for collection digital sampling plots: classifies plots
 * by a matrix (2D) or simple (1D) filter and colors them by class. The active
 * filter config lives in the store (`dspClassificationFilter`) so edits preview
 * live on the map.
 *
 * Sibling to CollectionHexagonColorFilter, sharing every child (editors, cost
 * summary, export modal) and the template/property/coverage helpers. Three
 * differences, all following from "a plot is an image":
 *   - no cell-size control — plot size is the image footprint;
 *   - the results table counts plots and labels area as footprint, since
 *     footprints overlap and their sum is not surveyed ground;
 *   - the report is the DSP one (Total Plots / Total Footprint Area) rather than
 *     the hexagon one, and there is no KML/GeoJSON export (the footprints are
 *     already downloadable from the project's own footprints layer).
 *
 * @param {object} props
 * @param {import('../../utils/collectionDspToPlots').DspPlot[]} props.plots
 * @param {object} props.results - per-class classification results, from the
 *   parent layer's useHexagonClassification call (shared, not recomputed here —
 *   the layer needs the same classification for map styling).
 * @param {string} props.collectionName
 * @param {string} props.collectionUuid
 * @param {Array<{key: string, label: string}>} props.objects - collection detection objects,
 *   listed as the report's detection classes.
 */
const CollectionDspColorFilter = ({ plots, results, collectionName, objects }) => {
	const { modifyLayersSidebarRef } = useProject();

	const filter = useCollectionStore(s => s.dspClassificationFilter);
	const setFilter = useCollectionStore(s => s.setDspClassificationFilter);

	const [projectNotes, setProjectNotes] = useState('');
	const [showEditor, setShowEditor] = useState(false);

	const currencies = useCurrencies();

	const availableProperties = useMemo(
		() => getAvailableProperties(plots, objects),
		[plots, objects]
	);
	const availableIds = useMemo(
		() => new Set(availableProperties.map(p => p.id)),
		[availableProperties]
	);

	const {
		activeTemplate,
		showSaveModal,
		setShowSaveModal,
		saveTitle,
		setSaveTitle,
		saveTemplate,
		saveMutation,
		deleteMutation,
		templateOptions,
		selectMode,
		handleTemplateSelect,
		handleDeleteTemplate,
	} = useHexagonFilterTemplates(filter, setFilter, NO_CELL_SIZE, availableIds);

	const isSimple = filter?.type === 'simple';

	// Resolved currency for price labels/report — falls back to the first entry
	// (UNIT), then to the shared default if currencies is ever empty.
	const currency =
		currencies.find(c => c.id === filter?.currency) || currencies[0] || DEFAULT_CURRENCY;

	// Whole-collection detection summary (every detected object, not just the
	// active filter's classes), used for the report's detection stats table.
	const summary = useMemo(() => collectionDspSummary(plots), [plots]);

	// Merge partial updates into the active filter, reading the latest from the
	// store to avoid stale-closure merges during rapid edits.
	const updateFilter = useCallback(
		updates => {
			const current = useCollectionStore.getState().dspClassificationFilter ?? {};
			setFilter({ ...current, ...updates });
		},
		[setFilter]
	);

	// Observed data range for the active simple property — the editor's hint text.
	const propertyRange = useMemo(
		() => (isSimple ? getPropertyRange(plots, filter?.property) : null),
		[isSimple, plots, filter?.property]
	);

	// Per-band observed threshold ranges — feeds the matrix coverage-gap check.
	const perBandThresholdRanges = useMemo(
		() =>
			!isSimple && filter
				? getPerBandThresholdRanges(
						plots,
						filter.bandProperty,
						filter.thresholdProperty,
						filter.bands
					)
				: [],
		[isSimple, filter, plots]
	);

	// Coverage gaps — computed once here so the same result drives both the
	// warning below AND the exported report (see useDspExport).
	const coverageGaps = useMemo(() => {
		if (!filter) return null;
		return isSimple
			? getSimpleFilterCoverageGaps(filter.classes, propertyRange)
			: getMatrixFilterCoverageGaps(filter, perBandThresholdRanges);
	}, [filter, isSimple, propertyRange, perBandThresholdRanges]);

	const coverageMessage = useMemo(
		() => describeCoverageGaps(filter, coverageGaps),
		[filter, coverageGaps]
	);

	// Every plot unclassified usually means the filter reads a property this run
	// has no data for — most often mean height, which needs a CHM. Coverage-gap
	// detection can't catch that (there is no observed range to leave a gap in),
	// so it's called out separately with the properties actually in play.
	const noDataMessage = useMemo(() => {
		if (!filter || !plots.length) return null;
		if (results.unclassified?.count !== plots.length) return null;
		const ids = isSimple ? [filter.property] : [filter.bandProperty, filter.thresholdProperty];
		const missing = ids.filter(id => id && !availableIds.has(id));
		if (!missing.length) return null;
		// The label comes from the full catalog, not `availableProperties` — a
		// missing property is by definition absent from the latter.
		const labels = missing
			.map(id => AVAILABLE_PROPERTIES.find(p => p.id === id)?.label ?? id)
			.join(' and ');
		return `No plot could be classified: ${labels} has no data in this collection. Pick another property in the editor — mean height, for example, needs a CHM.`;
	}, [filter, plots.length, results, isSimple, availableIds]);

	// Results rows: user classes (even at 0) then the special buckets when non-empty.
	const summaryRows = useMemo(() => {
		if (!filter) return [];
		const classRows = (filter.classes ?? []).map(c => results[c.id]).filter(Boolean);
		const specials = ['below_minimum', 'unclassified']
			.map(id => results[id])
			.filter(r => r?.count > 0);
		return [...classRows, ...specials];
	}, [filter, results]);

	const {
		showExportModal,
		setShowExportModal,
		exportTheme,
		setExportTheme,
		isExporting,
		handleExport,
	} = useDspExport({
		results,
		filter,
		collectionName,
		objects,
		projectNotes,
		currency,
		availableProperties,
		coverageGaps,
		summary,
	});

	if (!modifyLayersSidebarRef?.current) return null;

	return createPortal(
		<SidebarTemplate
			zindex="2"
			sidebarId={COLLECTION_SIDEBAR_IDS.dspColorFilter}
			header="DSP Classification & Price Estimation"
			width="wide"
			closeable>
			<Flex vertical gap={12} style={{ padding: '12px 16px' }}>
				{/* Plot tally — the DSP stand-in for the hexagon panel's cell-size
				    control: there is nothing to configure, but the sample size is
				    the first thing you need to read a per-plot statistic. */}
				<Flex align="center" justify="space-between" gap={8}>
					<Text style={{ fontSize: '0.8rem' }}>Sample plots</Text>
					<Text style={{ fontSize: '0.8rem' }}>{formatNumber(plots.length)}</Text>
				</Flex>

				<Divider style={{ margin: 0 }} />

				{/* Filter mode */}
				<Flex vertical gap={4}>
					<Text style={{ fontSize: '0.8rem' }}>Filter mode</Text>
					<Segmented
						block
						options={MODE_OPTIONS}
						value={filter?.type}
						onChange={selectMode}
					/>
				</Flex>

				{!filter && (
					<Text type="secondary" style={{ fontSize: '0.8rem' }}>
						Pick a mode to color plots by class, or load a saved template.
					</Text>
				)}

				{/* Filter template */}
				<Flex vertical gap={4}>
					<Text style={{ fontSize: '0.8rem' }}>Filter template</Text>
					<Select
						placeholder="-- Select template --"
						value={activeTemplate?.uuid}
						onChange={handleTemplateSelect}
						options={templateOptions}
						allowClear
						onClear={() => selectMode(filter?.type === 'simple' ? 'simple' : 'matrix')}
						notFoundContent="No saved templates"
					/>
					<Flex gap={8}>
						<Button
							icon={<LuSave />}
							onClick={() => {
								setSaveTitle(filter?.name ?? '');
								setShowSaveModal(true);
							}}
							disabled={!filter}
							style={{ flex: 1 }}>
							{activeTemplate ? 'Update template' : 'Save as template'}
						</Button>
						{activeTemplate && (
							<Button
								danger
								icon={<LuTrash2 />}
								loading={deleteMutation.isPending}
								onClick={handleDeleteTemplate}>
								Delete
							</Button>
						)}
					</Flex>
				</Flex>

				{filter && (
					<>
						<Divider style={{ margin: 0 }} />

						{/* Classification Results */}
						<Flex vertical gap={4}>
							<Text
								type="secondary"
								style={{ fontSize: '0.75rem', textTransform: 'uppercase' }}>
								Classification Results
							</Text>
							<Table
								size="small"
								pagination={false}
								columns={RESULTS_COLUMNS}
								dataSource={summaryRows}
								rowKey="id"
							/>
							{/* Footprints overlap, so the summed figure above is not the
							    surveyed area. Stated here rather than only in the report,
							    since this is where the number is first read. */}
							<Text type="secondary" style={{ fontSize: '0.7rem' }}>
								Footprint totals count overlapping ground once per image, so they
								exceed the surveyed area.
							</Text>
						</Flex>

						{noDataMessage && (
							<Alert
								type="warning"
								showIcon
								message={noDataMessage}
								style={{ fontSize: '0.75rem' }}
							/>
						)}

						{coverageMessage && (
							<Alert
								type="warning"
								showIcon
								message={coverageMessage}
								style={{ fontSize: '0.75rem' }}
							/>
						)}

						{/* Edit filter — opens the full editor (properties, classes,
						    pricing, notes) in a bottom drawer. */}
						<Button icon={<LuSettings />} onClick={() => setShowEditor(true)} block>
							Edit filter
						</Button>

						<Divider style={{ margin: 0 }} />

						<Button
							icon={<LuFileDown />}
							onClick={() => setShowExportModal(true)}
							block>
							Export Report
						</Button>
					</>
				)}
			</Flex>

			{/* Filter editor drawer — classification config on the left,
			    cost/notes/export on the right. Capped height + no mask so the map
			    stays visible and interactive behind it. */}
			<Drawer
				title={
					<FilterEditorHeader
						isSimple={isSimple}
						filter={filter}
						updateFilter={updateFilter}
						availableProperties={availableProperties}
					/>
				}
				placement="bottom"
				height="50vh"
				mask={false}
				open={showEditor}
				onClose={() => setShowEditor(false)}
				destroyOnHidden
				styles={{ body: { overflowY: 'auto' } }}>
				{filter && (
					<Flex gap={24} align="flex-start" wrap="wrap">
						{/* Left: classification config */}
						<Flex vertical style={{ flex: 2, minWidth: 320 }}>
							{isSimple ? (
								<SimpleEditor
									filter={filter}
									updateFilter={updateFilter}
									availableProperties={availableProperties}
									classificationResults={results}
									propertyRange={propertyRange}
									coverageMessage={coverageMessage}
									unitLabel="plots"
								/>
							) : (
								<MatrixEditor
									filter={filter}
									updateFilter={updateFilter}
									availableProperties={availableProperties}
									coverageMessage={coverageMessage}
								/>
							)}
						</Flex>

						{/* Right: cost summary → notes → export, in that order. Sits beside
						    the threshold grid so ranges and their cost read side by side;
						    min width covers the class rows' edit/remove controls. */}
						<Flex vertical gap={16} style={{ flex: 1, minWidth: 300 }}>
							<Flex vertical gap={4}>
								<CostSummary
									filter={filter}
									updateFilter={updateFilter}
									classificationResults={results}
									currency={currency}
									currencies={currencies}
								/>
								<Text type="secondary" style={{ fontSize: '0.7rem' }}>
									Priced on detected object area — an object photographed by two
									overlapping images is counted in both plots, so treat the total
									as an upper bound.
								</Text>
							</Flex>

							<Divider style={{ margin: 0 }} />

							{/* Project notes — included verbatim in the exported report. */}
							<Flex vertical gap={4}>
								<Text style={{ fontSize: '0.8rem' }}>Project notes (optional)</Text>
								<Input.TextArea
									rows={3}
									placeholder="Add notes about the project, calculations, or observations..."
									value={projectNotes}
									onChange={e => setProjectNotes(e.target.value)}
									style={{ fontSize: '0.85rem' }}
								/>
								<Text type="secondary" style={{ fontSize: '0.75rem' }}>
									Notes will be included in exported reports.
								</Text>
							</Flex>

							<Button
								type="primary"
								icon={<LuFileDown />}
								onClick={() => setShowExportModal(true)}
								block>
								Export Report
							</Button>
						</Flex>
					</Flex>
				)}
			</Drawer>

			{/* Save-template modal */}
			<Modal
				title={activeTemplate ? 'Update template' : 'Save template'}
				open={showSaveModal}
				onOk={() => saveTemplate({ title: saveTitle })}
				onCancel={() => setShowSaveModal(false)}
				okButtonProps={{ loading: saveMutation.isPending, disabled: !saveTitle.trim() }}
				okText="Save">
				<Input
					placeholder="Template name"
					value={saveTitle}
					onChange={e => setSaveTitle(e.target.value)}
					onPressEnter={() => saveTitle.trim() && saveTemplate({ title: saveTitle })}
				/>
			</Modal>

			{/* Export options modal */}
			<CollectionExportReportModal
				open={showExportModal}
				onClose={() => setShowExportModal(false)}
				exportTheme={exportTheme}
				setExportTheme={setExportTheme}
				onExport={handleExport}
				isExporting={isExporting}
			/>
		</SidebarTemplate>,
		modifyLayersSidebarRef.current
	);
};

export default CollectionDspColorFilter;
